import { useEffect } from 'react'
import { useLocation } from 'react-router'
import { isMarketingCookieEnabled } from 'src/analytics/gtm'
import { getAppContext } from 'src/context/AppContext'
import { isAndroidNativeApp, isIosNativeApp } from 'src/geo/locationService'
import { pages } from 'src/routing/pages'
import { ScreenSize } from 'src/ui/style/ScreenSize'

type EcomailQueue = NonNullable<Window['ecmwidget']>

const widgetScriptSrc = 'https://d70shl7vidtft.cloudfront.net/widget.js'
const trackingScriptSrc = 'https://d70shl7vidtft.cloudfront.net/ecmtr-2.4.2.js'
const trackingCollectorHost = 'd2dpiwfhf3tz0r.cloudfront.net'
const widgetAccount = 'boxyland'
const desktopWidgetId = '1-43c2cd496486bcc27217c3e790fb4088'
const mobileWidgetId = '2-2bb287d15897fe2f9d89c882af9a3a8b'
const insertedScriptAttr = 'data-ecomail-widget-loader'
const insertedTrackingScriptAttr = 'data-ecomail-tracking-loader'
const insertedStyleAttr = 'data-ecomail-widget-style'
const hiddenStyleAttr = 'data-ecomail-widget-hidden-style'
const mobileMaxWidth = ScreenSize.Sizes.md
const allowedPaths = [pages.Landing.path, pages.WarehouseSearch.path]
const warehouseDetailPathPattern = new RegExp(
    `^${pages.WarehouseDetail.path.replace(':id', '[^/]+')}$`
)
const widgetCleanupSelector = `#ec_form_${desktopWidgetId}, #ec_form_${mobileWidgetId}, #ec-v-from-popup-cover, #ec-v-from-mobile-popup-holder`
const mobilePopupSelector = `#ec_form_${mobileWidgetId} .ec-v-form-holder.ec-v-form-holder-mobile_popup`

const isAllowedPath = (pathname: string) =>
    allowedPaths.includes(pathname) || warehouseDetailPathPattern.test(pathname)

const hasInsertedNode = (tagName: 'script' | 'style', attrName: string) =>
    document.querySelector(`${tagName}[${attrName}="true"]`)

const appendBeforeFirstScript = (node: HTMLScriptElement) => {
    const firstScript = document.getElementsByTagName('script')[0]

    if (firstScript?.parentNode) {
        firstScript.parentNode.insertBefore(node, firstScript)
    } else {
        document.head.appendChild(node)
    }
}

const createQueue = (): EcomailQueue => {
    const queue = ((...args: unknown[]) => {
        ;(queue.q = queue.q || []).push(args)
    }) as EcomailQueue

    return queue
}

const setWidgetVisibility = (isVisible: boolean) => {
    const existingStyle = document.querySelector<HTMLStyleElement>(
        `style[${hiddenStyleAttr}="true"]`
    )

    if (isVisible) {
        existingStyle?.remove()
        return
    }

    const style = existingStyle || document.createElement('style')

    style.setAttribute(hiddenStyleAttr, 'true')
    style.innerHTML = `${widgetCleanupSelector} { display: none !important; }`

    if (!existingStyle) {
        document.head.appendChild(style)
    }
}

const resetMobilePopupPosition = (popup: HTMLElement | null) => {
    if (!popup) return

    popup.style.position = ''
    popup.style.top = ''
    popup.style.bottom = ''
    popup.style.left = ''
    popup.style.right = ''
    popup.style.width = ''
    popup.style.maxWidth = ''
    popup.style.margin = ''
    popup.style.transform = ''
    popup.style.zIndex = ''
}

const getWidgetId = () =>
    window.matchMedia(`(max-width: ${mobileMaxWidth}px)`).matches
        ? mobileWidgetId
        : desktopWidgetId

const insertWidget = () => {
    if (hasInsertedNode('script', insertedScriptAttr)) return
    if (isIosNativeApp() || isAndroidNativeApp()) return

    window['ecm-widget'] = 'ecmwidget'
    const queue = window.ecmwidget || createQueue()
    window.ecmwidget = queue

    const script = document.createElement('script')

    script.id = getWidgetId()
    script.dataset.a = widgetAccount
    script.src = widgetScriptSrc
    script.async = true
    script.setAttribute(insertedScriptAttr, 'true')

    appendBeforeFirstScript(script)
}

const insertTracking = () => {
    if (hasInsertedNode('script', insertedTrackingScriptAttr)) return

    if (!window.ecotrack) {
        window.GlobalSnowplowNamespace = window.GlobalSnowplowNamespace || []
        window.GlobalSnowplowNamespace.push('ecotrack')

        const queue = createQueue()

        queue.q = queue.q || []
        window.ecotrack = queue
    }

    const script = document.createElement('script')

    script.src = trackingScriptSrc
    script.async = true
    script.setAttribute(insertedTrackingScriptAttr, 'true')

    appendBeforeFirstScript(script)

    window.ecotrack('newTracker', 'cf', trackingCollectorHost, {
        appId: widgetAccount
    })
    window.ecotrack('setUserIdFromLocation', 'ecmid')
    window.ecotrack('trackPageView')
}

const insertWidgetStyle = () => {
    if (hasInsertedNode('style', insertedStyleAttr)) return

    const style = document.createElement('style')

    style.setAttribute(insertedStyleAttr, 'true')
    style.innerHTML = `
        @media (max-width: ${mobileMaxWidth}px) {
            #ec-v-from-popup-cover {
                position: fixed !important;
                inset: 0 !important;
                width: 100vw !important;
                height: 100dvh !important;
                box-sizing: border-box !important;
                padding: 0 !important;
                overflow: auto !important;
                z-index: 99999 !important;
            }

            #ec-v-from-popup-cover[style*='display: block'] {
                display: flex !important;
                align-items: center !important;
                justify-content: center !important;
            }

            #ec-v-from-popup-cover
                .ec-v-form-holder.ec-v-form-holder-popup {
                position: relative !important;
                inset: auto !important;
                bottom: auto !important;
                left: auto !important;
                margin: 0 !important;
                width: min(100%, 420px) !important;
                max-width: 100vw !important;
                max-height: none !important;
                box-sizing: border-box !important;
                overflow: visible !important;
                transform: none !important;
            }

            #ec_form_${desktopWidgetId}
                #ec-v-from-popup-cover
                .ec-v-form-holder-popup
                > form {
                min-height: 0 !important;
                height: auto !important;
            }

            #ec_form_${desktopWidgetId}
                #ec-v-from-popup-cover
                .ec-v-form-holder-popup
                .ec-v-form-step-visible {
                display: block !important;
                width: 100% !important;
                height: auto !important;
                min-height: 0 !important;
                overflow: visible !important;
            }

            #ec_form_${desktopWidgetId}
                #ec-v-from-popup-cover
                .ec-v-form-holder-popup
                .ec-v-form-step-visible
                > .ec-v-form-column {
                display: block !important;
                width: 100% !important;
                min-width: 0 !important;
                box-sizing: border-box !important;
            }

            #ec_form_${desktopWidgetId}
                #ec-v-from-popup-cover
                .ec-v-form-holder-popup
                .ec-v-form-step-visible
                > .ec-v-form-column
                > .ec-v-form-image[style*='background-image'] {
                display: none !important;
                width: 0 !important;
                height: 0 !important;
                padding: 0 !important;
            }

            #ec_form_${mobileWidgetId} #ec-v-from-mobile-popup-holder {
                position: fixed !important;
                inset: 0 !important;
                width: 100vw !important;
                height: 100dvh !important;
                box-sizing: border-box !important;
                padding: 0 !important;
                overflow: auto !important;
                z-index: 99999 !important;
            }

            #ec_form_${mobileWidgetId}
                #ec-v-from-mobile-popup-holder[style*='display: block'] {
                display: flex !important;
                align-items: flex-end !important;
                justify-content: center !important;
            }

            #ec_form_${mobileWidgetId}
                #ec-v-from-mobile-popup-holder
                .ec-v-form-holder.ec-v-form-holder-mobile_popup {
                position: relative !important;
                inset: auto !important;
                width: min(100%, 420px) !important;
                max-width: 100vw !important;
                height: auto !important;
                max-height: calc(100dvh) !important;
                box-sizing: border-box !important;
                overflow: auto !important;
                transform: none !important;
            }

            #ec_form_${mobileWidgetId}
                .ec-v-form-holder-mobile_popup
                #ec-v-form-close {
                top: 10px !important;
                right: 10px !important;
            }

            #ec_form_${mobileWidgetId}
                .ec-v-form-holder-mobile_popup
                .ec-v-form-step-visible
                > .ec-v-form-column:first-child
                > .ec-v-form-text:first-child {
                padding-right: 56px !important;
                padding-left: 56px !important;
            }

            #ec_form_${mobileWidgetId}
                #ec-v-from-mobile-popup-holder
                .ec-v-form-holder.ec-v-form-holder-mobile_popup {
                margin: 0 !important;
                height: auto !important;
                max-height: none !important;
                overflow: visible !important;
            }

            #ec_form_${mobileWidgetId}
                #ec-v-from-mobile-popup-holder
                .ec-v-form-holder.ec-v-form-holder-mobile_popup
                > form {
                min-height: 0 !important;
                height: auto !important;
                overflow: visible !important;
            }

            #ec_form_${mobileWidgetId}
                #ec-v-from-mobile-popup-holder
                .ec-v-form-holder.ec-v-form-holder-mobile_popup
                .ec-v-form-step-visible {
                height: auto !important;
                min-height: 0 !important;
                overflow: visible !important;
            }

            #ec_form_${desktopWidgetId}
                #ec-v-from-popup-cover
                .ec-v-form-holder-popup
                input,
            #ec_form_${desktopWidgetId}
                #ec-v-from-popup-cover
                .ec-v-form-holder-popup
                textarea,
            #ec_form_${mobileWidgetId}
                #ec-v-from-mobile-popup-holder
                .ec-v-form-holder-mobile_popup
                input,
            #ec_form_${mobileWidgetId}
                #ec-v-from-mobile-popup-holder
                .ec-v-form-holder-mobile_popup
                textarea {
                font-size: 16px !important;
            }
        }
    `

    document.head.appendChild(style)
}

export const EcomailWidget = () => {
    const location = useLocation()
    const {
        config: { ecomailEnabled }
    } = getAppContext()

    useEffect(() => {
        if (!isAllowedPath(location.pathname)) return

        const vv = window.visualViewport
        if (!vv) return

        const syncPopupPosition = () => {
            const popup =
                document.querySelector<HTMLElement>(mobilePopupSelector)

            if (!popup) return

            const visibleBottom = vv.offsetTop + vv.height
            const keyboardLift = Math.max(0, window.innerHeight - visibleBottom)
            const keyboardOpen = keyboardLift > 120

            if (!keyboardOpen) {
                resetMobilePopupPosition(popup)
                return
            }

            const rect = popup.getBoundingClientRect()
            const popupHeight = popup.offsetHeight
            const gap = 12
            const top = Math.max(
                gap,
                window.innerHeight - popupHeight - keyboardLift
            )

            popup.style.position = 'fixed'
            popup.style.top = `${top}px`
            popup.style.bottom = 'auto'
            popup.style.left = `${Math.round(rect.left)}px`
            popup.style.right = 'auto'
            popup.style.width = `${Math.round(rect.width)}px`
            popup.style.maxWidth = `${Math.round(rect.width)}px`
            popup.style.margin = '0'
            popup.style.transform = 'none'
            popup.style.zIndex = '99999'
        }

        const observer = new MutationObserver(syncPopupPosition)
        observer.observe(document.body, { childList: true, subtree: true })

        syncPopupPosition()
        vv.addEventListener('resize', syncPopupPosition)
        vv.addEventListener('scroll', syncPopupPosition)

        return () => {
            observer.disconnect()
            vv.removeEventListener('resize', syncPopupPosition)
            vv.removeEventListener('scroll', syncPopupPosition)
            resetMobilePopupPosition(
                document.querySelector<HTMLElement>(mobilePopupSelector)
            )
        }
    }, [location.pathname])

    useEffect(() => {
        if (!ecomailEnabled || !isAllowedPath(location.pathname)) {
            setWidgetVisibility(false)
            return
        }

        const loadIfAllowed = () => {
            if (!isMarketingCookieEnabled()) return false

            insertWidgetStyle()
            insertTracking()
            insertWidget()
            setWidgetVisibility(true)
            return true
        }

        if (loadIfAllowed()) {
            return () => setWidgetVisibility(false)
        }

        const intervalId = window.setInterval(() => {
            if (loadIfAllowed()) {
                window.clearInterval(intervalId)
            }
        }, 1000)

        return () => {
            window.clearInterval(intervalId)
            setWidgetVisibility(false)
        }
    }, [ecomailEnabled, location.pathname])

    return null
}
